/**
 * What a project's folders are, as one rule for every screen that lists the project's apps (Home's Apps and Start
 * all, the Apps tab, Preview, the service dropdown, the tab signals): a service is a folder under `services/`, or the
 * project root itself (`.`, the legacy single-app layout). Never one:
 *
 * - `brain/`: the project's knowledge base every project gets by default (brain/docs, brain/memory, brain/scripts,
 *   brain/inputs, ...). A project with only its brain is an empty project from the apps' point of view, also when the
 *   brain holds scripts or a package.json. It still shows as knowledge (Code tree, Files, the Brain gadget).
 * - `services/` itself, and dot-folders (`.mia/`, `.flow/`, `.git/`, ...): the project's metadata.
 *
 * vfs-service (src/preview/projectLayout.js) and the preview-runner (crates/core/src/project_layout.rs) apply the
 * same rule on their side.
 */

/** The project's knowledge base folder, at the project root. */
export const BRAIN_DIR = 'brain';

const clean = (value) => String(value ?? '').trim().replace(/\\/g, '/').replace(/^(\.\/)+/, '').replace(/^\/+|\/+$/g, '');

/**
 * Whether a configuration service name or project path is the project's brain (`brain`, `brain/docs`, `./brain/`),
 * not an app. `services/brain` is a real service folder that happens to be called brain: it is a service.
 */
export function isBrainPath(nameOrPath) {
  const value = clean(nameOrPath);
  return value === BRAIN_DIR || value.startsWith(`${BRAIN_DIR}/`);
}

/** Whether a top-level folder of the project could be a service in the oldest layout (every top-level folder one). */
export function isServiceFolderName(name) {
  const value = clean(name);
  return Boolean(value) && !value.includes('/') && !value.startsWith('.') && value !== 'services' && !isBrainPath(value);
}

/** Whether a configuration entry (`[key, service]` or the service) is never an app: the brain. */
export function isNotAService(key, service = null) {
  return isBrainPath(key) || isBrainPath(service?.name);
}

/**
 * The configuration's services without the ones that are never apps (the brain), for a configuration read now or
 * saved before this rule (a "brain" entry in this browser's registry, or from the oldest-layout fallback). Only
 * Flow's own vfs-backed entries are dropped: a service Console registered keeps its name. Returns the same object
 * when nothing was dropped.
 */
export function withoutNonServices(services) {
  if (!services || typeof services !== 'object') { return services; }
  const drop = Object.entries(services).filter(([key, service]) => isNotAService(key, service) && (!service || service.vfsBacked));
  if (drop.length === 0) { return services; }
  const kept = { ...services };
  drop.forEach(([key]) => { delete kept[key]; });
  return kept;
}

/**
 * The configuration's services with the project's service folders as they are now (hooks/useServiceFolders: the
 * store the Apps tab reads), so Home's Apps, Needs attention and the header's Start all list what Apps lists: a folder
 * the configuration does not know yet is added (`{ name, type: 'custom', vfsBacked: true }`, as the configuration
 * loader makes it), a vfs-backed `services/<name>` whose folder is gone is left out, anything else (a Console service,
 * the project root `.`) is kept. `folders` null (not read yet): the configuration's, without the brain.
 * Returns `[key, service]` entries, the configuration's order first.
 */
export function servicesWithFolders(services, folders) {
  const entries = Object.entries(withoutNonServices(services || {}) || {});
  if (!Array.isArray(folders)) { return entries; }
  const known = new Set(folders);
  const kept = entries.filter(([key, service]) => !(service?.vfsBacked && String(key).startsWith('services/') && !known.has(key)));
  const keys = new Set(kept.map(([key]) => key));
  const added = folders.filter((folder) => !keys.has(folder) && !isBrainPath(folder)).map((folder) => [folder, { name: folder, type: 'custom', vfsBacked: true }]);
  return [...kept, ...added];
}

/**
 * The configuration key a service is known by (`design-mockup`, `services/design-mockup` and the key itself all name
 * `services/design-mockup`), or null when the configuration has no such service.
 */
export function serviceKeyIn(services, name) {
  if (!services || !name) { return null; }
  const value = clean(name);
  if (services[value]) { return value; }
  const short = value.replace(/^services\//, '');
  if (services[`services/${short}`]) { return `services/${short}`; }
  return Object.keys(services).find((key) => key.replace(/^services\//, '') === short || services[key]?.name === value) || null;
}

/**
 * Where "Open preview" of a service lands (Apps' Open preview, "See what X sees"; Canvas handleOpenSharedPreview):
 * always the Preview tab on that service, from any page. `{ name, key, select, load }`: `name` the service's short
 * name (`design-mockup`); `key` its configuration key to select (`services/design-mockup`), null when it is the one
 * selected already or the configuration does not know it; `load` true when the configuration does not know it yet
 * (just added): it is read again and the service selected once it is there.
 */
export function openPreviewPlan(services, name, selectedName = null) {
  const short = clean(name).replace(/^services\//, '');
  if (!short) { return { name: null, key: null, load: false }; }
  const key = serviceKeyIn(services, short);
  return { name: short, key: key && key !== selectedName ? key : null, load: !key };
}
